import {
  type ConversationDraftResponse,
  type ConversationSendSlackResponse,
  conversationDraftResponseSchema,
  conversationSendSlackResponseSchema,
} from '@lms/shared-types'
import { apiRequest } from './client'

export type { ConversationSendSlackResponse }

export interface SendConversationToSlackBody {
  title: string
  opening: string
  talkingPoints: string[]
  questionsToAsk: string[]
  toneWarning: string | null
  nextStep: string
  invocationId: string | null
}

/**
 * Input shape accepted by `draftConversation`.
 *
 * `patternKinds` carries all detected pattern kinds for the employee so the
 * ai-service can compose a single conversation draft that addresses every
 * signal at once.
 */
export interface DraftConversationInput {
  patternKinds?: string[]
  notes?: string | null
  windowDays?: number
}

/**
 * Slice A3 follow-up — POST `/api/v1/employees/:id/conversation-send-slack`.
 * Posts the AI-drafted conversation prep to the manager's own Slack DM.
 */
export async function sendConversationToSlack(
  employeeId: string,
  body: SendConversationToSlackBody,
): Promise<ConversationSendSlackResponse> {
  const raw = await apiRequest<unknown>(
    `/api/v1/employees/${encodeURIComponent(employeeId)}/conversation-send-slack`,
    { method: 'POST', body },
  )
  return conversationSendSlackResponseSchema.parse(raw)
}

/**
 * Slice A3 — POST `/api/v1/employees/:id/conversation-draft`.
 *
 * Generates a five-field structured conversation draft the manager can edit
 * and share. RLS-guarded; manager/HOD on team or HR+ may call. The api
 * proxies to ai-service `/v1/conversation-draft`, which composes
 * `employee.pattern_summary`, `employee.leave_history_window`, and
 * `employee.conversation_history` metrics before asking the LLM.
 *
 * `patternKinds` is passed directly — the ai-service drafts one unified
 * conversation covering all detected patterns for the employee.
 */
export async function draftConversation(
  employeeId: string,
  { patternKinds = [], notes, windowDays = 90 }: DraftConversationInput,
): Promise<ConversationDraftResponse> {
  const raw = await apiRequest<unknown>(
    `/api/v1/employees/${encodeURIComponent(employeeId)}/conversation-draft`,
    {
      method: 'POST',
      body: {
        patternKinds,
        notes,
        windowDays,
      },
    },
  )
  return conversationDraftResponseSchema.parse(raw)
}
